import {UIGameManager} from '../../UIGameManager'
import {UIGameMapTileEdgeHighlightController} from '../ui_game_map_highlight/UIGameMapTileEdgeHighlightController'
import {GameStateEnums} from '../../../../../../shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIGameActionConfirmationParameters, UIHoverActionController} from '../../ui_game_hover_actions/UIHoverActionController'
import {UIGameMapHighlightParameters, UIGameMapHighlightView} from '../UIGameMapHighlightView'
import {PIXI} from '@colonist/pixi'
import {MapHoverController} from './UIGameMapHoverController'
import {BeginnerHintFormat, GameAction} from '@colonist/game-base-types'

export class UIGameMapTileEdgeHoverController extends UIGameMapTileEdgeHighlightController implements MapHoverController {
    private readonly shouldHighlightOnlyOnHover: boolean
    private readonly hoverMechanicActions: UIHoverActionController[]

    constructor(uiGameManager: UIGameManager, shouldHighlightOnlyOnHover: boolean) {
        super(uiGameManager)
        this.shouldHighlightOnlyOnHover = shouldHighlightOnlyOnHover
        this.hoverMechanicActions = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.GameEnd: this.removeHighlights(); break
            case UIGameEventEnums.BeginnerHintReceived: this.beginnerHintReceived(data); break
            case UIGameEventEnums.GameStateUpdated: this.destroyBeginnerHintView(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            default: break
        }
    }

    registerHoverAction(hoverAction: UIHoverActionController) {
        this.hoverMechanicActions.push(hoverAction)
    }

    onHoverStateChange(isHoverStateActive: boolean) {
        const beginnerHintActive = this.beginnerHintView != undefined
        this.removeHighlights()
        if(isHoverStateActive) this.addHoverState()
        if(!beginnerHintActive) return

        this.destroyBeginnerHintView()
        this.beginnerHintReceived(this.beginnerHint)
    }

    onHoverHighlightClick() {
        this.onHoverStateChange(true)
        this.endAnimation()
    }

    onHoverHighlightCancellation() {
        this.startAnimation()
    }

    protected override highlight(highlightParameters: UIGameMapHighlightParameters, action: () => void) {
        const highlightView = new UIGameMapHighlightView(this.uiGameManager, highlightParameters, this.uiGameManager.mapController.mapView, action, false, this.shouldHighlightOnlyOnHover)
        if(this.shouldHighlightOnlyOnHover) highlightView.displayOnlyOnHover()
        this.highlightViews.push(highlightView)
    }

    private addHoverState() {
        const newRoadEdges: number[] = []
        const newShipEdges: number[] = []

        this.hoverMechanicActions.forEach((mechanicAction: UIHoverActionController) => {
            if(mechanicAction.isCornerAction) return

            if(mechanicAction.isShipAction) newShipEdges.push(...mechanicAction.validLocations)
            else newRoadEdges.push(...mechanicAction.validLocations)
        })

        this.roadEdges = newRoadEdges
        this.shipEdges = newShipEdges

        this.setRoadEdges(this.roadEdges)
        this.setShipEdges(this.shipEdges)
        this.updateHighlights()
    }

    protected override getActionForEdgeHighlight(highlightParameters: UIGameMapHighlightParameters, index: number): () => void {
        const mechanicsAssociatedWithEdge = this.getMechanicsAssociatedWithEdge(index)
        return () => {
            if(this.currentBeginnerModeAction) this.currentBeginnerModeAction.guideText.options = {...this.currentBeginnerModeAction?.guideText.options, context: 'WantToBuildRoad'}
            if(!this.isAllowedPlacementIndex(index)) return this.uiGameManager.eventController.sendEvent(this.currentBeginnerModeAction?.guideText, GameStateEnums.UIGameEventEnums.BeginnerModeForceOpenGuideBubble)
            this.uiGameManager.mapController.hoverController.onHoverStateChange()
            this.destroyBeginnerHintView()
            const buttonSize = highlightParameters.size * 1.75 * this.buttonScaleForMobile
            const highlightCenter = new PIXI.Point(highlightParameters.x + highlightParameters.size / 2, highlightParameters.y + highlightParameters.size / 2)
            const buttonX = highlightCenter.x - buttonSize / 2
            const buttonY = highlightCenter.y - (buttonSize * 1.5)
            this.uiGameManager.mapController.hoverController.onHoverHighlightClick()
            const cancelAction = () => {
                this.uiGameManager.mapController.hoverController.onHoverHighlightCancellation()
            }
            const confirmationParameters: UIGameActionConfirmationParameters[] = []
            mechanicsAssociatedWithEdge.forEach((mechanic: UIHoverActionController) => confirmationParameters.push(mechanic.getConfirmationParameters(index)))
            this.confirmActionController.showActionConfirmation(highlightParameters, buttonX, buttonY, buttonSize, cancelAction, confirmationParameters, index)
        }
    }

    protected override beginnerHintReceived(data: BeginnerHintFormat): void {
        if(data.type != GameAction.ConfirmBuildRoad) return
        super.beginnerHintReceived(data)
    }

    private getMechanicsAssociatedWithEdge(index: number): UIHoverActionController[] {
        const allowedMechanics: UIHoverActionController[] = []
        for(const mechanic of this.hoverMechanicActions) {
            if(mechanic.isCornerAction) continue
            if(mechanic.validLocations.includes(index)) allowedMechanics.push(mechanic)
        }
        return allowedMechanics
    }
}
